// src/components/routes/Home.jsx
import React from "react";
import { Link } from "react-router-dom";
import "../../css/home.css";
import SEO from "../utils/SEO.jsx";

function HomeOld() {
  return (
    <div className="lp-snap">
      <SEO
        title="Plot Division | Build Better Stories"
        description="A creative writing platform for planning books, building worlds, tracking characters, and sharing stories."
        url="https://plotdivision.com"
      />
      {/* SECTION 1 — HERO */}
      <section className="lp-sec lp-hero">
        <div className="lp-wrap">
          <div className="lp-card glass lp-hero-card">
            <div className="lp-hero-grid">
              <div className="lp-hero-left">
                <div className="lp-badge">Plot Division</div>

                <h1 className="lp-title">
                  Build your story world like a{" "}
                  <span className="lp-title-accent">pro</span>.
                </h1>

                <p className="lp-subtitle">
                  Organize characters, arcs, subplots, timelines, and notes in
                  one place — with a clean “liquid glass” feel that keeps you
                  focused.
                </p>

                <div className="lp-cta">
                  <Link to="/register" className="lp-btn lp-btn-primary">
                    Create free account
                  </Link>
                  <Link to="/login" className="lp-btn lp-btn-ghost">
                    Log in
                  </Link>
                </div>

                <div className="lp-trust">
                  <div className="lp-trust-item">
                    <span className="lp-dot" />
                    Save ideas instantly
                  </div>
                  <div className="lp-trust-item">
                    <span className="lp-dot" />
                    Track arcs + relationships
                  </div>
                  <div className="lp-trust-item">
                    <span className="lp-dot" />
                    Upgrade when you’re ready
                  </div>
                </div>
              </div>

              <div className="lp-hero-right">
                <div className="lp-media">
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="/img/home-hero-640.webp 640w, /img/home-hero-960.webp 960w, /img/home-hero-1280.webp 1280w"
                      sizes="(max-width: 767px) calc(100vw - 64px), (max-width: 1199px) 44vw, 540px"
                    />
                    <img
                      className="lp-img"
                      src="/img/home-hero-960.webp"
                      srcSet="/img/home-hero-640.webp 640w, /img/home-hero-960.webp 960w, /img/home-hero-1280.webp 1280w"
                      sizes="(max-width: 767px) calc(100vw - 64px), (max-width: 1199px) 44vw, 540px"
                      alt="Writing workspace"
                      width="1400"
                      height="1051"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                    />
                  </picture>
                  <div className="lp-media-overlay">
                    <div className="lp-kicker">One place for your story bible</div>
                    <div className="lp-overlay-row">
                      <span className="lp-chip">Characters</span>
                      <span className="lp-chip">Arcs</span>
                      <span className="lp-chip">Timeline</span>
                    </div>
                  </div>
                </div>

                <div className="lp-scrollhint">Scroll ↓</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — FEATURES */}
      <section className="lp-sec lp-features">
        <div className="lp-wrap">
          <div className="lp-card glass lp-split">
            <div className="lp-split-left">
              <h2 className="lp-h2">Everything you need to stay consistent</h2>
              <p className="lp-p">
                Stop losing notes across apps. Keep your character web, plot
                roadmap, and story rules together — so your world stays coherent.
              </p>

              <div className="lp-feature-list">
                <div className="lp-feature">
                  <div className="lp-ico">🧠</div>
                  <div>
                    <div className="lp-feature-title">Story brain</div>
                    <div className="lp-feature-text">
                      Lore, rules, and key details in one place.
                    </div>
                  </div>
                </div>

                <div className="lp-feature">
                  <div className="lp-ico">🧩</div>
                  <div>
                    <div className="lp-feature-title">Plot clarity</div>
                    <div className="lp-feature-text">
                      Keep subplots organized so pacing stays strong.
                    </div>
                  </div>
                </div>

                <div className="lp-feature">
                  <div className="lp-ico">🧵</div>
                  <div>
                    <div className="lp-feature-title">Character web</div>
                    <div className="lp-feature-text">
                      Track relationships without messy spreadsheets.
                    </div>
                  </div>
                </div>

                <div className="lp-feature">
                  <div className="lp-ico">⏳</div>
                  <div>
                    <div className="lp-feature-title">Timeline flow</div>
                    <div className="lp-feature-text">
                      View events in order — no contradictions.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lp-split-right">
              <div className="lp-media tall">
                <img
                  className="lp-img"
                  src="/img/home-hero-960.webp"
                  alt="Plot Division story planning board"
                  width="960"
                  height="721"
                  loading="lazy"
                  decoding="async"
                />
                <div className="lp-media-overlay">
                  <div className="lp-kicker">Your world stays connected</div>
                  <div className="lp-overlay-row">
                    <span className="lp-chip">Notes</span>
                    <span className="lp-chip">Subplots</span>
                    <span className="lp-chip">Scenes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — SOCIAL PROOF / CTA */}
      <section className="lp-sec lp-proof">
        <div className="lp-wrap">
          <div className="lp-card glass lp-proof-card">
            <div className="lp-proof-grid">
              <div className="lp-proof-left">
                <h2 className="lp-h2">Make your story world feel real</h2>
                <p className="lp-p">
                  Join free and start building your Bookcase. Upgrade later if
                  you want bigger limits and premium tools.
                </p>

                <div className="lp-cta">
                  <Link to="/register" className="lp-btn lp-btn-primary">
                    Create free account
                  </Link>
                  <Link to="/support" className="lp-btn lp-btn-ghost">
                    Ask Support
                  </Link>
                </div>

                <div className="lp-mini-note">
                  No pressure — start small, grow when you’re ready.
                </div>
              </div>

              <div className="lp-proof-right">
                <div className="lp-quotes">
                  <div className="lp-quote">
                    <div className="lp-quote-text">
                      “My characters finally make sense — the relationships view
                      is a lifesaver.”
                    </div>
                    <div className="lp-quote-by">— Writer in progress</div>
                  </div>

                  <div className="lp-quote">
                    <div className="lp-quote-text">
                      “I stopped rewriting notes. Everything stays organized and
                      consistent.”
                    </div>
                    <div className="lp-quote-by">— Plot nerd</div>
                  </div>
                </div>

                <div className="lp-media short">
                  <img
                    className="lp-img"
                    src="/img/home-hero-640.webp"
                    alt="Plot Division writing workspace"
                    width="640"
                    height="481"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomeOld;
